import React, { useState } from 'react';
import { IonDatetime } from '@ionic/react';
import type { DashboardWidget } from '../services/dashboard';
import { Button } from './ui/Button';
import { ModalSection } from './ui/Modal';
import { TextInput } from './ui/TextInput';
import { FormSelect, type SelectOption } from './ui/FormSelect';
import { List } from './ui/List';
import { ColorPicker } from './ui/ColorPicker';

// Background color presets - suitable for widget/section backgrounds
const BACKGROUND_COLOR_PRESETS = [
  // Transparent / None
  'transparent',
  // Dark theme backgrounds
  '#0f172a', // Slate 900
  '#1e293b', // Slate 800
  '#334155', // Slate 700
  // Light backgrounds
  '#ffffff', // White
  '#f8fafc', // Slate 50
  '#f1f5f9', // Slate 100
  // Subtle colored backgrounds
  '#1e3a5f', // Dark blue
  '#14532d', // Dark green
  '#7c2d12', // Dark orange
  '#581c87', // Dark purple
  '#991b1b', // Dark red
  // Accent colors (muted)
  '#3b82f6', // Blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#ef4444', // Red
];

interface WidgetConfigFormProps {
  widget: DashboardWidget;
  onSave: (config: Record<string, unknown>) => void;
  onCancel: () => void;
}

// Size presets for spacing options (gap, padding)
const SIZE_PRESETS: Record<string, number> = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

// Get preset key from a value, or 'custom' if not a preset
// Handles both numbers and strings (for custom CSS values like "1.5rem")
const getPresetKey = (value: number | string | undefined, defaultPreset: string = 'md'): string => {
  if (value === undefined) return defaultPreset;
  // If it's a string (custom CSS value), it's custom
  if (typeof value === 'string') return 'custom';
  // Check if it matches a preset
  const entry = Object.entries(SIZE_PRESETS).find(([_, v]) => v === value);
  return entry ? entry[0] : 'custom';
};

// Define config options for each widget type
const WIDGET_CONFIG_SCHEMA: Record<string, Array<{
  key: string;
  label: string;
  type: 'toggle' | 'select' | 'text' | 'number' | 'date' | 'textarea' | 'size-preset' | 'color';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  defaultValue?: unknown;
}>> = {
  clock: [
    {
      key: 'format',
      label: 'Clock Style',
      type: 'select',
      options: [
        { value: 'digital', label: 'Digital' },
        { value: 'analog', label: 'Analog' },
      ],
      defaultValue: 'digital',
    },
    { key: 'use24Hour', label: '24-Hour Format', type: 'toggle', defaultValue: false },
    { key: 'showSeconds', label: 'Show Seconds', type: 'toggle', defaultValue: false },
    { key: 'showDate', label: 'Show Date', type: 'toggle', defaultValue: true },
  ],
  weather: [
    {
      key: 'units',
      label: 'Temperature Units',
      type: 'select',
      options: [
        { value: 'imperial', label: 'Fahrenheit (°F)' },
        { value: 'metric', label: 'Celsius (°C)' },
      ],
      defaultValue: 'imperial',
    },
    { key: 'showForecast', label: 'Show Forecast', type: 'toggle', defaultValue: true },
    { key: 'forecastDays', label: 'Forecast Days', type: 'number', defaultValue: 5 },
  ],
  countdown: [
    { key: 'title', label: 'Event Title', type: 'text', placeholder: 'e.g., Vacation', defaultValue: '' },
    { key: 'targetDate', label: 'Target Date', type: 'date', defaultValue: '' },
  ],
  photo_frame: [
    { key: 'albumId', label: 'Album ID', type: 'text', placeholder: 'Leave empty for all photos' },
    { key: 'interval', label: 'Slide Interval (seconds)', type: 'number', defaultValue: 10 },
    {
      key: 'transition',
      label: 'Transition Effect',
      type: 'select',
      options: [
        { value: 'fade', label: 'Fade' },
        { value: 'slide', label: 'Slide' },
      ],
      defaultValue: 'fade',
    },
  ],
  iframe: [
    { key: 'url', label: 'URL', type: 'text', placeholder: 'https://example.com' },
    { key: 'refreshInterval', label: 'Refresh Interval (seconds)', type: 'number', defaultValue: 0 },
  ],
  notes: [
    { key: 'content', label: 'Note Content', type: 'textarea', placeholder: 'Enter your note...' },
    {
      key: 'color',
      label: 'Note Color',
      type: 'select',
      options: [
        { value: 'yellow', label: 'Yellow' },
        { value: 'pink', label: 'Pink' },
        { value: 'blue', label: 'Blue' },
        { value: 'green', label: 'Green' },
        { value: 'purple', label: 'Purple' },
      ],
      defaultValue: 'yellow',
    },
  ],
  travel_times: [
    { key: 'hoursAhead', label: 'Hours to Look Ahead', type: 'number', defaultValue: 2 },
  ],
  commute: [
    { key: 'hoursAhead', label: 'Hours to Look Ahead', type: 'number', defaultValue: 2 },
  ],
  inspiration: [
    {
      key: 'source',
      label: 'Quote Source',
      type: 'select',
      options: [
        { value: 'all', label: 'All Sources' },
        { value: 'custom', label: 'Custom Quotes Only' },
        { value: 'api', label: 'API Quotes Only' },
      ],
      defaultValue: 'all',
    },
  ],
  // Unified calendar widget with all view types
  calendar: [
    {
      key: 'viewType',
      label: 'View Type',
      type: 'select',
      options: [
        { value: 'agenda', label: 'Agenda (List)' },
        { value: 'day', label: 'Day View' },
        { value: 'week', label: 'Week View' },
        { value: 'month', label: 'Month View' },
      ],
      defaultValue: 'agenda',
    },
    { key: 'daysAhead', label: 'Days Ahead (Agenda)', type: 'number', defaultValue: 7 },
    { key: 'maxEvents', label: 'Max Events (Agenda)', type: 'number', defaultValue: 10 },
    { key: 'startHour', label: 'Start Hour (Day/Week)', type: 'number', defaultValue: 6 },
    { key: 'endHour', label: 'End Hour (Day/Week)', type: 'number', defaultValue: 22 },
    { key: 'showAllDay', label: 'Show All-Day Events', type: 'toggle', defaultValue: true },
    { key: 'showWeekends', label: 'Show Weekends (Week/Month)', type: 'toggle', defaultValue: true },
    { key: 'showViewPicker', label: 'Show View Picker', type: 'toggle', defaultValue: false },
  ],
  // Legacy calendar widgets - kept for backwards compatibility
  agenda: [
    { key: 'daysAhead', label: 'Days to Show', type: 'number', defaultValue: 7 },
    { key: 'maxEvents', label: 'Max Events', type: 'number', defaultValue: 10 },
  ],
  calendar_day: [
    { key: 'maxEvents', label: 'Max Events', type: 'number', defaultValue: 8 },
  ],
  calendar_week: [
    { key: 'showWeekends', label: 'Show Weekends', type: 'toggle', defaultValue: true },
  ],
  // Enhanced unified chores widget
  chores: [
    {
      key: 'viewType',
      label: 'View Type',
      type: 'select',
      options: [
        { value: 'summary', label: 'Summary (Progress)' },
        { value: 'list', label: 'List (By Member)' },
        { value: 'grid', label: 'Grid (Cards)' },
        { value: 'tabbed', label: 'Tabbed (By Person)' },
      ],
      defaultValue: 'summary',
    },
    { key: 'showCompleted', label: 'Show Completed Chores', type: 'toggle', defaultValue: true },
    { key: 'showProgress', label: 'Show Progress Bar', type: 'toggle', defaultValue: true },
    { key: 'showPoints', label: 'Show Points', type: 'toggle', defaultValue: true },
    { key: 'showViewPicker', label: 'Show View Picker', type: 'toggle', defaultValue: false },
    { key: 'enableCelebration', label: 'Enable Celebration Animation', type: 'toggle', defaultValue: true },
    { key: 'compactMode', label: 'Compact Mode', type: 'toggle', defaultValue: false },
  ],
  // Legacy chores widgets - kept for backwards compatibility
  chores_today: [
    { key: 'showCompleted', label: 'Show Completed', type: 'toggle', defaultValue: true },
    { key: 'groupByMember', label: 'Group by Family Member', type: 'toggle', defaultValue: true },
  ],
  chores_board: [
    { key: 'showPoints', label: 'Show Points', type: 'toggle', defaultValue: true },
  ],
  grocery_list: [
    { key: 'showChecked', label: 'Show Purchased Items', type: 'toggle', defaultValue: false },
    { key: 'groupByCategory', label: 'Group by Category', type: 'toggle', defaultValue: true },
  ],
  announcements: [
    { key: 'maxItems', label: 'Max Announcements', type: 'number', defaultValue: 5 },
    {
      key: 'autoRotateInterval',
      label: 'Auto-Rotate Speed',
      type: 'select',
      options: [
        { value: '5000', label: 'Fast (5s)' },
        { value: '10000', label: 'Normal (10s)' },
        { value: '15000', label: 'Slow (15s)' },
        { value: '30000', label: 'Very Slow (30s)' },
        { value: '0', label: 'No Auto-Rotate' },
      ],
      defaultValue: '10000',
    },
    { key: 'showDismissButton', label: 'Show Dismiss Button', type: 'toggle', defaultValue: true },
  ],
  // Visual Schedule widget for kids' routines
  visual_schedule: [
    {
      key: 'category',
      label: 'Schedule Category',
      type: 'select',
      options: [
        { value: 'morning', label: 'Morning Routine' },
        { value: 'after_school', label: 'After School' },
        { value: 'evening', label: 'Evening' },
        { value: 'bedtime', label: 'Bedtime' },
        { value: 'weekend', label: 'Weekend' },
        { value: 'anytime', label: 'Any Time' },
      ],
      defaultValue: 'anytime',
    },
    {
      key: 'memberTypes',
      label: 'Show Members',
      type: 'select',
      options: [
        { value: 'children', label: 'Children & Teens' },
        { value: 'all', label: 'All Members' },
        { value: 'adults', label: 'Adults Only' },
        { value: 'child', label: 'Children Only' },
        { value: 'teen', label: 'Teens Only' },
      ],
      defaultValue: 'children',
    },
    {
      key: 'progressVariant',
      label: 'Progress Style',
      type: 'select',
      options: [
        { value: 'bar', label: 'Progress Bar' },
        { value: 'dots', label: 'Dots' },
        { value: 'steps', label: 'Steps' },
      ],
      defaultValue: 'bar',
    },
    {
      key: 'itemSize',
      label: 'Item Size',
      type: 'select',
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large (Touch-friendly)' },
      ],
      defaultValue: 'large',
    },
    {
      key: 'layout',
      label: 'Layout',
      type: 'select',
      options: [
        { value: 'list', label: 'List' },
        { value: 'grid', label: 'Grid' },
      ],
      defaultValue: 'list',
    },
    {
      key: 'rotateInterval',
      label: 'Auto-Rotate Interval (seconds)',
      type: 'number',
      defaultValue: 30,
    },
    { key: 'showHeader', label: 'Show Header', type: 'toggle', defaultValue: true },
    { key: 'showProgress', label: 'Show Progress', type: 'toggle', defaultValue: true },
    { key: 'enableCelebrations', label: 'Enable Celebrations', type: 'toggle', defaultValue: true },
    { key: 'celebrateEachTask', label: 'Celebrate Each Task', type: 'toggle', defaultValue: true },
    { key: 'autoRotate', label: 'Auto-Rotate Members', type: 'toggle', defaultValue: false },
    { key: 'transparent', label: 'Transparent Background', type: 'toggle', defaultValue: false },
  ],
  section: [
    // Note: layout, showTitle, showDivider, collapsible, defaultExpanded are in the quick-access popover menu
    // This modal shows advanced options only (spacing, styling)
    { key: 'label', label: 'Section Name', type: 'text', placeholder: 'Section', defaultValue: 'Section' },
    { key: 'gap', label: 'Column Gap', type: 'size-preset', defaultValue: 16 },
    { key: 'padding', label: 'Inner Padding', type: 'size-preset', defaultValue: 16 },
    { key: 'backgroundColor', label: 'Background Color', type: 'color' },
    {
      key: 'shadow',
      label: 'Shadow',
      type: 'select',
      options: [
        { value: 'none', label: 'None' },
        { value: 'sm', label: 'Small' },
        { value: 'md', label: 'Medium' },
        { value: 'lg', label: 'Large' },
      ],
      defaultValue: 'none',
    },
  ],
};

// Size preset options for the dropdown
const SIZE_PRESET_OPTIONS: SelectOption[] = [
  { id: 'none', label: 'None (0px)', value: 'none' },
  { id: 'xs', label: 'XS (4px)', value: 'xs' },
  { id: 'sm', label: 'SM (8px)', value: 'sm' },
  { id: 'md', label: 'MD (16px)', value: 'md' },
  { id: 'lg', label: 'LG (24px)', value: 'lg' },
  { id: 'xl', label: 'XL (32px)', value: 'xl' },
  { id: 'custom', label: 'Custom...', value: 'custom' },
];

const WidgetConfigForm: React.FC<WidgetConfigFormProps> = ({ widget, onSave, onCancel }) => {
  const schema = WIDGET_CONFIG_SCHEMA[widget.widget_type] || [];

  // Initialize config state from widget config or defaults
  const [config, setConfig] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = { ...widget.config };

    // Convert columns array to comma-separated string for editing
    if (Array.isArray(initial.columns)) {
      initial.columns = initial.columns.join(', ');
      // If columns is set, show 'custom' in columnCount dropdown
      if (!initial.columnCount) {
        initial.columnCount = 'custom';
      }
    }

    schema.forEach(field => {
      if (initial[field.key] === undefined && field.defaultValue !== undefined) {
        initial[field.key] = field.defaultValue;
      }
    });
    return initial;
  });

  // Track which size-preset fields are in custom mode
  const [customModeFields, setCustomModeFields] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    schema.forEach(field => {
      if (field.type === 'size-preset') {
        const value = widget.config?.[field.key] as number | string | undefined;
        initial[field.key] = getPresetKey(value) === 'custom';
      }
    });
    return initial;
  });

  const handleChange = (key: string, value: unknown) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Handle size preset selection
  const handleSizePresetChange = (key: string, presetKey: string) => {
    if (presetKey === 'custom') {
      // Switch to custom mode, keep current value
      setCustomModeFields(prev => ({ ...prev, [key]: true }));
    } else {
      // Use preset value
      setCustomModeFields(prev => ({ ...prev, [key]: false }));
      handleChange(key, SIZE_PRESETS[presetKey]);
    }
  };

  // Handle custom size input - accepts any CSS value (px, rem, em, %, etc.)
  const handleCustomSizeChange = (key: string, value: string) => {
    // Store as-is - can be "16", "1.5rem", "2em", "10%", etc.
    handleChange(key, value);
  };

  const handleSubmit = () => {
    // Convert numeric string values to actual numbers for specific fields
    const processedConfig = { ...config };

    // Handle columnCount - convert string to number, or handle 'custom'
    if (processedConfig.columnCount && typeof processedConfig.columnCount === 'string') {
      if (processedConfig.columnCount === 'custom') {
        // When using custom widths, set columnCount to undefined so columns array takes precedence
        delete processedConfig.columnCount;
      } else {
        processedConfig.columnCount = parseInt(processedConfig.columnCount, 10);
      }
    }

    // Handle columns - convert comma-separated string to array of numbers
    if (processedConfig.columns && typeof processedConfig.columns === 'string') {
      const columnsStr = processedConfig.columns as string;
      if (columnsStr.trim()) {
        const columnsArray = columnsStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
        if (columnsArray.length > 0) {
          processedConfig.columns = columnsArray;
        } else {
          delete processedConfig.columns;
        }
      } else {
        delete processedConfig.columns;
      }
    }

    onSave(processedConfig);
  };

  // Separate toggle fields from other fields
  const toggleFields = schema.filter(f => f.type === 'toggle');
  const otherFields = schema.filter(f => f.type !== 'toggle');

  if (schema.length === 0) {
    return (
      <ModalSection>
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--ion-color-medium)' }}>
          <p>No configuration options available for this widget type.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', padding: '0 16px 16px' }}>
          <Button fill="outline" onClick={onCancel} style={{ flex: 1 }}>
            Close
          </Button>
        </div>
      </ModalSection>
    );
  }

  // Convert options to FormSelect format
  const toSelectOptions = (options: Array<{ value: string; label: string }>): SelectOption[] => {
    return options.map(opt => ({
      id: opt.value,
      label: opt.label,
      value: opt.value,
    }));
  };

  return (
    <ModalSection>
      {/* Non-toggle fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px' }}>
        {otherFields.map(field => (
          <div key={field.key}>
            {field.type === 'select' && field.options && (
              <FormSelect
                label={field.label}
                value=""
                onChange={() => {}}
                options={toSelectOptions(field.options)}
                selectedOption={toSelectOptions(field.options).find(
                  opt => opt.value === String(config[field.key])
                ) || null}
                onSelect={(opt) => handleChange(field.key, opt.value)}
                placeholder={`Select ${field.label.toLowerCase()}...`}
                variant="default"
                size="md"
                searchable={false}
              />
            )}

            {field.type === 'text' && (
              <TextInput
                label={field.label}
                value={(config[field.key] as string) || ''}
                onChange={(value) => handleChange(field.key, value)}
                placeholder={field.placeholder}
                variant="default"
                size="md"
              />
            )}

            {field.type === 'number' && (
              <TextInput
                label={field.label}
                type="number"
                value={String(config[field.key] ?? '')}
                onChange={(value) => handleChange(field.key, value ? parseFloat(value) : 0)}
                placeholder={field.placeholder}
                variant="default"
                size="md"
              />
            )}

            {field.type === 'textarea' && (
              <TextInput
                label={field.label}
                value={(config[field.key] as string) || ''}
                onChange={(value) => handleChange(field.key, value)}
                placeholder={field.placeholder}
                variant="default"
                size="md"
                multiline
                rows={4}
              />
            )}

            {field.type === 'date' && (
              <div>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--ion-color-medium)',
                    marginBottom: 6,
                  }}
                >
                  {field.label}
                </label>
                <IonDatetime
                  value={config[field.key] as string}
                  onIonChange={e => handleChange(field.key, e.detail.value)}
                  presentation="date"
                  style={{
                    borderRadius: 10,
                    border: '1.5px solid var(--ion-color-step-200)',
                  }}
                />
              </div>
            )}

            {field.type === 'size-preset' && (
              <div>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--ion-color-medium)',
                    marginBottom: 6,
                  }}
                >
                  {field.label}
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FormSelect
                    value=""
                    onChange={() => {}}
                    options={SIZE_PRESET_OPTIONS}
                    selectedOption={
                      customModeFields[field.key]
                        ? SIZE_PRESET_OPTIONS.find(o => o.value === 'custom') || null
                        : SIZE_PRESET_OPTIONS.find(
                            o => o.value === getPresetKey(config[field.key] as number | string | undefined)
                          ) || null
                    }
                    onSelect={(opt) => handleSizePresetChange(field.key, opt.value as string)}
                    placeholder="Select size..."
                    variant="default"
                    size="md"
                    searchable={false}
                    style={{ flex: 1 }}
                  />
                  {customModeFields[field.key] && (
                    <TextInput
                      value={String(config[field.key] ?? '')}
                      onChange={(value) => handleCustomSizeChange(field.key, value)}
                      placeholder="e.g. 20px, 1.5rem"
                      variant="default"
                      size="md"
                      style={{ width: '120px' }}
                    />
                  )}
                </div>
                {customModeFields[field.key] && (
                  <p style={{
                    fontSize: 11,
                    color: 'var(--ion-color-medium)',
                    marginTop: 4,
                    marginBottom: 0,
                  }}>
                    Supports: px, rem, em, %, vh, vw
                  </p>
                )}
              </div>
            )}

            {field.type === 'color' && (
              <ColorPicker
                label={field.label}
                value={(config[field.key] as string) || null}
                onChange={(color) => handleChange(field.key, color)}
                presetColors={BACKGROUND_COLOR_PRESETS}
                showCustomInput={true}
                columns={6}
                swatchSize={32}
                storageKey={`widget-config-${field.key}-swatches`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Toggle fields as a list */}
      {toggleFields.length > 0 && (
        <List
          variant="plain"
          size="md"
          lines="inset"
          items={toggleFields.map(field => ({
            id: field.key,
            label: field.label,
            interactive: false,
            end: {
              toggle: {
                checked: !!config[field.key],
                onChange: (checked) => handleChange(field.key, checked),
              },
            },
          }))}
        />
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', padding: '16px' }}>
        <Button fill="outline" onClick={onCancel} style={{ flex: 1 }}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} style={{ flex: 1 }}>
          Save
        </Button>
      </div>
    </ModalSection>
  );
};

export default WidgetConfigForm;
